import React from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ClassificationLevel } from '@core/generated/gql-types';
import {
  actionListboxButtonMdClassName,
  actionListboxOptionActiveClassName,
} from '@core/theme/actionTheme.util';
import { ClassificationCard } from './types';

interface ClassificationCardItemProps {
  card: ClassificationCard;
  isPersisting: boolean;
  onAssignLevel: (card: ClassificationCard, level: ClassificationLevel) => void;
  onEdit: (card: ClassificationCard) => void;
  onDelete: (card: ClassificationCard) => void;
}

const ClassificationCardItem: React.FC<ClassificationCardItemProps> = ({
  card,
  isPersisting,
  onAssignLevel,
  onEdit,
  onDelete,
}) => {
  const isUnset = !card.classificationLevel;
  const cardLevels =
    card.classification.classificationLevels?.filter(Boolean) || [];

  return (
    <div
      className={`group relative flex min-h-[88px] flex-col justify-between rounded-lg border p-3 ${
        isUnset ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="absolute top-1/2 right-2 flex -translate-y-1/2 flex-col gap-1 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
        <button
          type="button"
          onClick={() => onEdit(card)}
          disabled={isPersisting}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
          title="Edit"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(card)}
          disabled={isPersisting}
          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
          title="Delete"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="pr-12">
        {isUnset ? (
          <Listbox
            value={undefined}
            onChange={(level: ClassificationLevel) =>
              onAssignLevel(card, level)
            }
            disabled={isPersisting}
          >
            <div className="relative">
              <Listbox.Button
                className={`${actionListboxButtonMdClassName} disabled:bg-gray-100`}
              >
                <span className="block truncate text-gray-400">
                  Assign level
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                </span>
              </Listbox.Button>
              <Transition
                as={React.Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
                  {cardLevels.map((level) => (
                    <Listbox.Option
                      key={level?.id}
                      value={level}
                      className={({ active }) =>
                        `relative cursor-default py-2 pr-4 pl-10 select-none ${
                          active
                            ? actionListboxOptionActiveClassName
                            : 'text-gray-900'
                        }`
                      }
                    >
                      <span className="block truncate">{level?.name}</span>
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
        ) : (
          <p className="text-l font-semibold text-gray-900">
            {card.classificationLevel?.name}
          </p>
        )}
      </div>
      <p className="mt-2 text-sm text-gray-500">
        {card.classificationGroup.name} • {card.classification.name}
      </p>
    </div>
  );
};

export default ClassificationCardItem;
